import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiGetSports, apiGetCyclePhases, apiGetPhaseInsights } from '../services/api';
import PageHeader from '../components/PageHeader';
import PeriodSelector, { readStoredPeriod } from '../components/PeriodSelector';
import ViewToggle from '../components/ViewToggle';
import { filterLastNDays, filterWindow, periodWindow, windowDays } from '../utils/dateRange';
import PeriodPager, { usePeriodOffset } from '../components/PeriodPager';
import { ReactComponent as ActivityIcon } from '../assets/common/activity_icon.svg';
import { ReactComponent as EsouflementIcon } from '../assets/sport/essouflement_icon.svg';
import InfoButton from '../components/InfoButton';
import { DAY_LABELS } from '../constants/labels';
import { PHASE_API_MAP, PHASE_COLORS_MAP, SPORT, UI } from '../constants/colors';
import { monotonePath } from '../utils/chartPaths';
import { sportEmoji } from '../constants/sports';
import ErrorBanner from '../components/ErrorBanner';
import './Sport.css';
import { toISODate, todayISO } from '../utils/formDate';
import PhaseProfile from '../components/PhaseProfile';
import SymptomBar from '../components/SymptomBar';
// `autres` = jours d'activité sans phase connue. L'endpoint /phases ne renvoie
// une phase que pour les dates où une entrée Cycle existe : sans ce groupe de
// repli, tous ces jours disparaissaient de la vue groupée (widget vide quand la
// sportive ne remplit pas son cycle).
const PHASE_LABELS_SPORT = {
  regles: 'Règles', folliculaire: 'Folliculaire', ovulation: 'Ovulation',
  luteale: 'Lutéale', autres: 'Hors phase identifiée',
};
const PHASE_KEYS_SPORT = ['regles', 'folliculaire', 'ovulation', 'luteale', 'autres'];
const BREATH_EMOJIS = { 'Pas essoufflée': '😌', 'Modérée': '😤', 'Très essoufflée': '😮‍💨' };

// Hauteur déduite du viewBox, jamais fixée : une hauteur fixe sur un viewBox de 420 de
// large réduisait le tracé à ~0,72 sur téléphone et le centrait dans sa boîte, avec
// ~35 px de vide dessus et dessous et des axes à 7–9 px.
const CHART_SVG_STYLE = { display: 'block', height: 'auto' };

const smoothPath = (points) => {
  if (points.length < 2) return '';
  const tension = 0.3;
  let d = `M ${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const p0 = points[i - 2] || points[i - 1];
    const p1 = points[i - 1], p2 = points[i];
    const p3 = points[i + 1] || p2;
    const cp1x = p1.x + (p2.x - p0.x) * tension;
    const cp1y = p1.y + (p2.y - p0.y) * tension;
    const cp2x = p2.x - (p3.x - p1.x) * tension;
    const cp2y = p2.y - (p3.y - p1.y) * tension;
    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  return d;
};


const Sport = () => {
  const { user, getToken } = useAuth();
  const [sports, setSports] = useState([]);
  const [insights, setInsights] = useState([]);
  const [phases, setPhases] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupSportByPhase, setGroupSportByPhase] = useState(true);
  const [historyChart, setHistoryChart] = useState(null); // null | 'hr' | 'rpe' | 'breath'
  const [historyRange, setHistoryRange] = useState(90);
  const [sportActivityRangeDays, setSportActivityRangeDays] = useState(() => readStoredPeriod('period:sport:activity', 30));

   const [hrRangeDays, setHrRangeDays] = useState(() => readStoredPeriod('period:sport:hr', 30));
   const [rpeRangeDays, setRpeRangeDays] = useState(() => readStoredPeriod('period:sport:rpe', 30));
   const [breathRangeDays, setBreathRangeDays] = useState(() => readStoredPeriod('period:sport:breath', 30));

  // Période affichée par widget : 0 = celle qui finit aujourd'hui, 1 = la précédente…
  // (flèches ‹ › et glissement au doigt, cf. PeriodPager).
  const [hrOffset, setHrOffset] = usePeriodOffset(hrRangeDays);
  const [rpeOffset, setRpeOffset] = usePeriodOffset(rpeRangeDays);
  const [breathOffset, setBreathOffset] = usePeriodOffset(breathRangeDays);
  const [activityOffset, setActivityOffset] = usePeriodOffset(sportActivityRangeDays);

  const loadSportData = useCallback(() => {
    if (!user?.id) { setLoading(false); return; }
    const token = getToken();
    setLoading(true);
    setError(null);
    Promise.all([
      apiGetSports(user.id, token),
      apiGetCyclePhases(user.id, token).catch(() => []),
      apiGetPhaseInsights(user.id, token).catch(() => []),
    ]).then(([sportData, phaseData, insightData]) => {
      setInsights(insightData || []);
      setSports(sportData);
      const lookup = {};
      (phaseData || []).forEach(p => {
        lookup[String(p.date)] = PHASE_API_MAP[p.phase] || p.phase.toLowerCase();
      });
      setPhases(lookup);
    }).catch(() => setError('Impossible de charger tes données. Vérifie ta connexion et réessaie.'))
      .finally(() => setLoading(false));
  }, [user?.id, getToken]);

  useEffect(() => {
    loadSportData();
  }, [loadSportData]);

   const sportsByDateAsc = useMemo(() => {
     return [...sports].sort((a, b) => String(a.date).localeCompare(String(b.date)));
   }, [sports]);

   // Premier relevé : borne du passé pour la navigation par période.
   const earliestSport = sportsByDateAsc[0]?.date ?? null;

   const sportsHrRange = useMemo(
     () => filterWindow(sportsByDateAsc, periodWindow(hrRangeDays, hrOffset), (s) => s.date),
     [sportsByDateAsc, hrRangeDays, hrOffset]
   );

   const rpeWindow = useMemo(() => periodWindow(rpeRangeDays, rpeOffset), [rpeRangeDays, rpeOffset]);
   const sportsRpeRange = useMemo(
     () => filterWindow(sportsByDateAsc, rpeWindow, (s) => s.date),
     [sportsByDateAsc, rpeWindow]
   );

   const sportsBreathRange = useMemo(
     () => filterWindow(sportsByDateAsc, periodWindow(breathRangeDays, breathOffset), (s) => s.date),
     [sportsByDateAsc, breathRangeDays, breathOffset]
   );

  const sportByDate = Object.fromEntries(sports.map(s => [s.date, s]));

  // Last 7 days
  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - 6 + i);
    return d;
  });

  // Stats
  const rpeEntries = sportsRpeRange.filter(s => s.rpe != null);
  const avgRpe = rpeEntries.length
    ? (rpeEntries.reduce((a, s) => a + s.rpe, 0) / rpeEntries.length).toFixed(1)
    : null;
  const restHrEntries = sportsHrRange.filter(s => s.rest_heart_rate != null);
  const avgRestHR = restHrEntries.length
    ? Math.round(restHrEntries.reduce((a, s) => a + s.rest_heart_rate, 0) / restHrEntries.length)
    : null;
  const effortHrEntries = sportsHrRange.filter(s => s.effort_heart_rate != null);
  const avgEffortHR = effortHrEntries.length
    ? Math.round(effortHrEntries.reduce((a, s) => a + s.effort_heart_rate, 0) / effortHrEntries.length)
    : null;

  const historyData = useMemo(
    () => filterLastNDays(sportsByDateAsc, historyRange, s => s.date),
    [sportsByDateAsc, historyRange]
  );
  const historyHrData = historyData;
  const historyRpeData = historyData;
  const historyBreathData = historyData;

  const sportsActivityRange = useMemo(
    () => filterWindow(sportsByDateAsc, periodWindow(sportActivityRangeDays, activityOffset), s => s.date),
    [sportsByDateAsc, sportActivityRangeDays, activityOffset]
  );

  // Référence sur 3 périodes, arrêtée au même jour que la période affichée.
  const sportsTripleActivityRange = useMemo(
    () => filterWindow(
      sportsByDateAsc,
      periodWindow(sportActivityRangeDays, activityOffset, sportActivityRangeDays * 3),
      s => s.date,
    ),
    [sportsByDateAsc, sportActivityRangeDays, activityOffset]
  );

  // Count days each sport was practiced in current period
  const sportActivityList = useMemo(() => {
    const freq = {};
    sportsActivityRange.forEach(s => {
      (s.sports_pratiques || []).forEach(sport => {
        freq[sport] = (freq[sport] || 0) + 1;
      });
    });
    const tripleFreq = {};
    sportsTripleActivityRange.forEach(s => {
      (s.sports_pratiques || []).forEach(sport => {
        tripleFreq[sport] = (tripleFreq[sport] || 0) + 1;
      });
    });
    const maxDays = Math.max(1, ...Object.values(freq));
    return Object.entries(freq)
      .sort((a, b) => b[1] - a[1])
      .map(([label, days]) => ({
        emoji: sportEmoji(label),
        label,
        days,
        pct: Math.round((days / sportActivityRangeDays) * 100),
        avgDays: Math.round((tripleFreq[label] || 0) / 3),
        avgPct: Math.round(((tripleFreq[label] || 0) / 3 / sportActivityRangeDays) * 100),
        maxDays,
      }));
  }, [sportsActivityRange, sportsTripleActivityRange, sportActivityRangeDays]);

  // Sports practiced grouped by cycle phase (dynamic, based on actual data)
  const sportsByPhaseMap = useMemo(() => {
    const result = { regles: {}, folliculaire: {}, ovulation: {}, luteale: {}, autres: {} };
    sportsActivityRange.forEach(s => {
      const phase = phases[String(s.date)];
      const bucket = phase && result[phase] != null ? phase : 'autres';
      (s.sports_pratiques || []).forEach(sport => {
        result[bucket][sport] = (result[bucket][sport] || 0) + 1;
      });
    });
    return result;
  }, [sportsActivityRange, phases]);

  return (
    <div className="sport-page">
      <PageHeader title="Activité Sportive" />

      {error && <ErrorBanner message={error} onRetry={loadSportData} />}

      {/* Weekly calendar widget */}
      <div className="sport-page__widget">
        <div className="sport-page__week-row">
          {last7.map((d, i) => {
            const dayLabel = DAY_LABELS[(d.getDay() + 6) % 7];
            return (
              <div key={i} className="sport-page__week-cell">
                <span className="sport-page__week-day">{dayLabel}</span>
                <span className="sport-page__week-num">{d.getDate()}</span>
              </div>
            );
          })}
        </div>
        <div className="sport-page__week-emojis">
          {last7.map((d, i) => {
            const dateStr = toISODate(d);
            const entry = sportByDate[dateStr];
            let value = '—';
            let colorClass = '';
            if (entry) {
              if (entry.is_rest_day) {
                value = 'Zzz';
                colorClass = ' sport-page__week-emoji-cell--rest';
              } else if (entry.rpe != null) {
                value = String(entry.rpe);
                colorClass = entry.rpe > 5
                  ? ' sport-page__week-emoji-cell--rpe-high'
                  : ' sport-page__week-emoji-cell--rpe-low';
              }
            }
            const isToday = dateStr === todayISO();
            return (
              <div key={i} className={`sport-page__week-emoji-cell${colorClass}${isToday ? ' sport-page__week-emoji-cell--active' : ''}`}>
                <span className="sport-page__week-emoji">{value}</span>
              </div>
            );
          })}
        </div>
        <p className="sport-page__week-legend">Le chiffre = RPE du jour · 💤 = Repos</p>
      </div>

      {/* Widget Fréquence cardiaque */}
      {/* Profil par phase — sport croisé avec le cycle, sur 6 mois */}
      <div className="sport-page__widget">
        <div className="sport-page__widget-header">
          <div className="sport-page__widget-icon sport-page__widget-icon--activity">
            <ActivityIcon />
          </div>
          <div className="sport-page__widget-title-group">
            <h3>Ton profil par phase</h3>
            <p className="sport-page__widget-subtitle">Sur tes 6 derniers mois</p>
          </div>
        </div>
        <PhaseProfile insights={insights} domain="sport" />
      </div>

      <div className="sport-page__widget">
        <div className="sport-page__widget-header">
          <div className="sport-page__widget-icon sport-page__widget-icon--activity">
            <ActivityIcon />
          </div>
          <div className="sport-page__widget-title-group">
            <h3 className="sport-page__chart-title-clickable" onClick={() => setHistoryChart('hr')}>Fréquence cardiaque<InfoButton infoKey="fcChart" /></h3>
            <p className="sport-page__widget-subtitle">Repos & Effort</p>
          </div>
          <div className="sport-page__hr-avg-block">
            <div className="sport-page__hr-avg">
              <span className="sport-page__hr-avg-value" style={{ color: 'var(--sport-repos)' }}>{avgRestHR ?? '—'}</span>
              <span className="sport-page__hr-avg-label">Repos</span>
            </div>
            <div className="sport-page__hr-avg">
              <span className="sport-page__hr-avg-value" style={{ color: 'var(--sport)' }}>{avgEffortHR ?? '—'}</span>
              <span className="sport-page__hr-avg-label">Effort</span>
            </div>
          </div>
          <PeriodSelector
            value={hrRangeDays}
            onChange={setHrRangeDays}
            storageKey="period:sport:hr"
            className="sport-page__widget-badge"
            arrowClassName="sport-page__badge-arrow"
          />
        </div>
        <PeriodPager days={hrRangeDays} offset={hrOffset} onOffsetChange={setHrOffset} earliest={earliestSport}>
          {sportsHrRange.length === 0 ? (
            <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '2rem 0' }}>Aucune donnée</p>
          ) : (() => {
            const hrData = sportsHrRange;
            const Y_MIN = 0;
            const Y_MAX = 200;
            const PLOT_X0 = 50;
            const PLOT_X1 = 390;
            const PLOT_Y0 = 10;
            const PLOT_Y1 = 210;
            const plotW = PLOT_X1 - PLOT_X0;
            const plotH = PLOT_Y1 - PLOT_Y0;
            const toY = (val) => PLOT_Y1 - ((val - Y_MIN) / (Y_MAX - Y_MIN)) * plotH;
            const toX = (i) => PLOT_X0 + (i / Math.max(hrData.length - 1, 1)) * plotW;

            const makePointsArr = (field) => hrData
              .map((s, i) => s[field] != null ? { x: toX(i), y: toY(s[field]) } : null)
              .filter(Boolean);

            const restPts = makePointsArr('rest_heart_rate');
            const effortPts = makePointsArr('effort_heart_rate');

            const yLabels = [0, 40, 80, 120, 160];

            return (
              <div style={{ padding: '0.5rem 0' }}>
                <svg width="100%" viewBox="0 0 420 252" style={CHART_SVG_STYLE}>
                  {/* Y-axis labels & grid lines */}
                  {yLabels.map(v => {
                    const y = toY(v);
                    return (
                      <g key={v}>
                        <text x="40" y={y + 4} fontSize="12" fill={UI.textAxis} textAnchor="end">{v}</text>
                        <line x1={PLOT_X0} y1={y} x2={PLOT_X1} y2={y} stroke="var(--color-grid)" strokeWidth="1" />
                      </g>
                    );
                  })}

                  {/* FC Effort line */}
                  {effortPts.length >= 2 && (
                    <path d={smoothPath(effortPts)} fill="none" stroke="var(--sport)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  )}

                  {/* FC Repos line */}
                  {restPts.length >= 2 && (
                    <path d={smoothPath(restPts)} fill="none" stroke="var(--sport-repos)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  )}

                  {/* X-axis day labels + phase dots */}
                  {(() => {
                    const labelStep = hrData.length <= 7 ? 1 : hrData.length <= 15 ? 2 : 3;
                    return hrData.map((s, i) => {
                      const dayLabel = ['D', 'L', 'M', 'M', 'J', 'V', 'S'][new Date(s.date).getDay()];
                      const phaseColor = phases[String(s.date)] ? (PHASE_COLORS_MAP[phases[String(s.date)]] || UI.surfaceEmpty) : UI.surfaceEmpty;
                      return (
                        <g key={i}>
                          {i % labelStep === 0 && <text x={toX(i)} y="230" fontSize="13" fill={UI.textAxis} textAnchor="middle">{dayLabel}</text>}
                          <circle cx={toX(i)} cy="244" r="4" fill={phaseColor} />
                        </g>
                      );
                    });
                  })()}
                </svg>

                <div className="sport-page__legend" style={{ justifyContent: 'center' }}>
                  <div className="sport-page__legend-item">
                    <span style={{ display: 'inline-block', width: 20, height: 3, borderRadius: 2, background: 'var(--sport-repos)', flexShrink: 0 }} />
                    <span>FC Repos</span>
                  </div>
                  <div className="sport-page__legend-item">
                    <span style={{ display: 'inline-block', width: 20, height: 3, borderRadius: 2, background: 'var(--sport)', flexShrink: 0 }} />
                    <span>FC Effort</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </PeriodPager>
      </div>

      {/* Widget RPE – Ressenti */}
      <div className="sport-page__widget">
        <div className="sport-page__widget-header">
          <div className="sport-page__widget-icon sport-page__widget-icon--activity">
            <ActivityIcon />
          </div>
          <div className="sport-page__widget-title-group">
            <h3 className="sport-page__chart-title-clickable" onClick={() => setHistoryChart('rpe')}>RPE –<InfoButton infoKey="rpeChart" /></h3>
            <p className="sport-page__widget-subtitle">Ressenti</p>
          </div>
          <div className="sport-page__rpe-score-block">
            <span
              className="sport-page__rpe-score"
              style={avgRpe != null ? { color: Number(avgRpe) > 5 ? SPORT.rpeIntense : SPORT.rpeFacile } : undefined}
            >
              {avgRpe ?? '—'}
            </span>
            <span className="sport-page__rpe-avg">
              {avgRpe == null ? 'Moyenne' : `Moyenne · ${Number(avgRpe) > 5 ? 'Intense' : 'Modéré'}`}
            </span>
          </div>
          <PeriodSelector
            value={rpeRangeDays}
            onChange={setRpeRangeDays}
            storageKey="period:sport:rpe"
            className="sport-page__widget-badge"
            arrowClassName="sport-page__badge-arrow"
          />
        </div>
        <PeriodPager days={rpeRangeDays} offset={rpeOffset} onOffsetChange={setRpeOffset} earliest={earliestSport}>
          {!sportsRpeRange.some(e => e.rpe != null) ? (
            <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '1rem 0' }}>Aucune donnée RPE</p>
          ) : (() => {
            const byDate = Object.fromEntries(sportsRpeRange.map(s => [String(s.date), s]));
            const rpeBars = [];
            windowDays(rpeWindow).forEach((d) => {
              const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
              const entry = byDate[dateStr];
              rpeBars.push({
                date: dateStr,
                dayLabel: DAY_LABELS[(d.getDay() + 6) % 7],
                rpe: entry?.rpe ?? null,
                isRest: !entry || !!entry.is_rest_day,
              });
            });
            const n = rpeBars.length;
            const labelStep = n <= 7 ? 1 : n <= 15 ? 2 : 3;
            const X0 = 20, X1 = 290, Y_TOP = 10, Y_BOT = 160;
            const plotW = X1 - X0;
            const plotH = Y_BOT - Y_TOP;
            const toX = (i) => X0 + (i / Math.max(n - 1, 1)) * plotW;
            const toY = (rpe) => Y_BOT - (rpe / 10) * plotH;
            const BAR_W = Math.max(4, Math.min(16, Math.floor(plotW / Math.max(n, 1)) - 2));
            return (
              <svg width="100%" height="195" viewBox="0 0 310 195">
                <defs>
                  <pattern id="restStripe" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                    <rect width="2.5" height="5" fill="var(--sport-repos)" fillOpacity="0.5" />
                  </pattern>
                </defs>
                {[10, 5, 0].map(v => (
                  <g key={v}>
                    <text x={X0 - 4} y={toY(v) + 4} fontSize="11" fill={UI.textAxis} textAnchor="end">{v}</text>
                    <line x1={X0} y1={toY(v)} x2={X1} y2={toY(v)} stroke="var(--color-grid)" strokeWidth="1" />
                  </g>
                ))}
                {rpeBars.map((entry, i) => {
                  const barColor = entry.rpe != null ? (entry.rpe <= 5 ? 'var(--sport-rpe-facile)' : 'var(--sport-primary)') : 'transparent';
                  const phaseColor = phases[entry.date] ? (PHASE_COLORS_MAP[phases[entry.date]] || UI.surfaceEmpty) : UI.surfaceEmpty;
                  const barH = entry.rpe != null ? (entry.rpe / 10) * plotH : 0;
                  return (
                    <g key={i}>
                      {entry.rpe != null ? (
                        <rect x={toX(i) - BAR_W / 2} y={toY(entry.rpe)} width={BAR_W} height={barH} rx="3" fill={barColor} />
                      ) : entry.isRest ? (
                        <rect x={toX(i) - BAR_W / 2} y={Y_BOT - 10} width={BAR_W} height={10} rx="2" fill="url(#restStripe)" />
                      ) : null}
                      {i % labelStep === 0 && <text x={toX(i)} y="175" fontSize="10" fill={UI.textAxis} textAnchor="middle">{entry.dayLabel}</text>}
                      <circle cx={toX(i)} cy="188" r="4" fill={phaseColor} />
                    </g>
                  );
                })}
              </svg>
            );
          })()}
        </PeriodPager>
      </div>

      {/* Widget Sports pratiqués */}
      <div className="sport-page__widget">
        <div className="sport-page__widget-header">
          <div className="sport-page__widget-icon sport-page__widget-icon--activity">
            <ActivityIcon />
          </div>
          <div className="sport-page__widget-title-group">
            <h3>Sports pratiqués</h3>
            <p className="sport-page__widget-subtitle">Fréquence des activités sur la période</p>
          </div>
          <div className="view-controls">
            <ViewToggle grouped={groupSportByPhase} onChange={setGroupSportByPhase} />
            <PeriodSelector
              value={sportActivityRangeDays}
              onChange={setSportActivityRangeDays}
              storageKey="period:sport:activity"
              className="sport-page__widget-badge"
              arrowClassName="sport-page__badge-arrow"
            />
          </div>
        </div>
        <PeriodPager days={sportActivityRangeDays} offset={activityOffset} onOffsetChange={setActivityOffset} earliest={earliestSport}>
          {sportActivityList.length === 0 ? (
            <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '1rem 0', fontSize: '0.85rem' }}>
              Aucun sport enregistré sur cette période
            </p>
          ) : groupSportByPhase ? (
            <div className="phase-groups">
              {PHASE_KEYS_SPORT.map(phaseKey => {
                const entries = sportsByPhaseMap[phaseKey];
                const totalInPhase = Object.values(entries).reduce((a, b) => a + b, 0);
                if (totalInPhase === 0) return null;
                const phaseColor = PHASE_COLORS_MAP[phaseKey] || UI.textSecondary;
                const sorted = Object.entries(entries).sort((a, b) => b[1] - a[1]);
                return (
                  <div key={phaseKey} className="phase-bar" style={{ '--phase-bar-color': phaseColor }}>
                    <span className="phase-group-label" style={{ color: phaseColor }}>
                      {PHASE_LABELS_SPORT[phaseKey]}
                    </span>
                    <div className="symptom-bar-list">
                      {sorted.map(([label, days], i) => {
                        const globalItem = sportActivityList.find(s => s.label === label);
                        return (
                          <SymptomBar
                            key={i}
                            emoji={sportEmoji(label)}
                            label={label}
                            days={days}
                            accentColor={SPORT.rpeIntense}
                            pct={Math.round((days / sportActivityRangeDays) * 100)}
                            avgPct={globalItem ? globalItem.avgPct : 0}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="symptom-bar-list">
              {sportActivityList.map((s, i) => (
                <SymptomBar
                  key={i}
                  emoji={s.emoji}
                  label={s.label}
                  days={s.days}
                  accentColor={SPORT.rpeIntense}
                  pct={s.pct}
                  avgPct={s.avgPct}
                />
              ))}
            </div>
          )}
        </PeriodPager>
      </div>

      {/* Widget Essoufflement */}
      <div className="sport-page__widget">
        <div className="sport-page__widget-header">
          <div className="sport-page__widget-icon sport-page__widget-icon--ess">
            <EsouflementIcon />
          </div>
          <div className="sport-page__widget-title-group">
            <h3 className="sport-page__chart-title-clickable" onClick={() => setHistoryChart('breath')}>Essoufflement<InfoButton infoKey="essoufflementChart" /></h3>
            <p className="sport-page__widget-subtitle">Pendant l'effort</p>
          </div>
          <PeriodSelector
            value={breathRangeDays}
            onChange={setBreathRangeDays}
            storageKey="period:sport:breath"
            className="sport-page__widget-badge"
            arrowClassName="sport-page__badge-arrow"
          />
        </div>
        <PeriodPager days={breathRangeDays} offset={breathOffset} onOffsetChange={setBreathOffset} earliest={earliestSport}>
          {sportsBreathRange.length === 0 ? (
            <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '1rem 0' }}>Aucune donnée</p>
          ) : (() => {
            const BREATH_LEVELS = { 'Pas essoufflée': 1, 'Modérée': 2, 'Très essoufflée': 3 };
            const LEVEL_COLORS = { 0: SPORT.soufflement.repos, 1: SPORT.soufflement.faible, 2: SPORT.soufflement.modere, 3: SPORT.soufflement.intense };
            // Repos = no data; rest days WITH breathlessness still get their level
            const breathDays = sportsBreathRange.map(s => ({
              dayLabel: DAY_LABELS[(new Date(s.date).getDay() + 6) % 7],
              level: s.breathlessness ? (BREATH_LEVELS[s.breathlessness] ?? 0) : 0,
              date: String(s.date),
            }));
            const n = breathDays.length;
            const labelStep = n <= 7 ? 1 : n <= 15 ? 2 : 3;

            const PLOT_X0 = 48, PLOT_X1 = 282, PLOT_Y0 = 32, PLOT_Y1 = 132;
            const toX = (i) => PLOT_X0 + (i / Math.max(n - 1, 1)) * (PLOT_X1 - PLOT_X0);
            const toY = (level) => PLOT_Y1 - ((level + 1) / 4) * (PLOT_Y1 - PLOT_Y0);
            const pts = breathDays.map((d, i) => ({ x: toX(i), y: toY(d.level) }));

            const linePath = monotonePath(pts);
            const areaPath = linePath + ` L ${pts[n-1].x},${PLOT_Y1} L ${pts[0].x},${PLOT_Y1} Z`;
            const Y_LABELS = { 0: 'Repos', 1: 'Faible', 2: 'Modéré', 3: 'Intense' };

            return (
              <div style={{ padding: '0.5rem 0' }}>
                <svg width="100%" height="194" viewBox="0 0 300 194">
                  <defs>
                    {/* Gradient matches the 4 level colors bottom→top */}
                    <linearGradient id="breathGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor={SPORT.soufflement.intense} stopOpacity="0.5" />
                      <stop offset="33%"  stopColor={SPORT.soufflement.modere} />
                      <stop offset="66%"  stopColor={SPORT.soufflement.faible} stopOpacity="0.3" />
                      <stop offset="100%" stopColor={SPORT.soufflement.repos} stopOpacity="0.2" />
                    </linearGradient>
                  </defs>
                  {[0,1,2,3].map(level => (
                    <g key={level}>
                      <text x={PLOT_X0 - 4} y={toY(level) + 4} fontSize="9" fill={UI.textAxis} textAnchor="end">{Y_LABELS[level]}</text>
                      <line x1={PLOT_X0} y1={toY(level)} x2={PLOT_X1} y2={toY(level)} stroke="var(--color-grid)" strokeWidth="1" />
                    </g>
                  ))}
                  <path d={areaPath} fill="url(#breathGrad)" />
                  <path d={linePath} fill="none" stroke="var(--sport-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  {breathDays.map((d, i) => {
                    const phaseColor = phases[d.date] ? (PHASE_COLORS_MAP[phases[d.date]] || UI.surfaceEmpty) : UI.surfaceEmpty;
                    return (
                      <g key={i}>
                        <text x={toX(i)} y="172" fontSize="10" fill={UI.textAxis} textAnchor="middle">
                          {i % labelStep === 0 ? d.dayLabel : ''}
                        </text>
                        <circle cx={toX(i)} cy="185" r="4" fill={phaseColor} />
                      </g>
                    );
                  })}
                </svg>
                <div style={{ display: 'flex', justifyContent: 'space-around', padding: '0.25rem 0 0', flexWrap: 'wrap' }}>
                  {[{ color: SPORT.soufflement.repos, label: 'Repos' }, { color: SPORT.soufflement.faible, label: 'Faible' }, { color: SPORT.soufflement.modere, label: 'Modéré' }, { color: SPORT.soufflement.intense, label: 'Intense' }].map(({ color, label }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', color: UI.textFaint }}>
                      <span style={{ display: 'inline-block', width: 22, height: 6, borderRadius: 3, background: color }} />
                      {label}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </PeriodPager>
      </div>
      {/* History modal */}
      {historyChart && (() => {
        const TITLES = { hr: 'Fréquence cardiaque', rpe: 'RPE – Ressenti', breath: 'Essoufflement' };
        const renderHistoryContent = () => {
          if (historyChart === 'hr') {
            const hrData = historyHrData;
            if (hrData.length === 0) return <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '2rem 0' }}>Aucune donnée</p>;
            const Y_MIN = 0, Y_MAX = 200, PLOT_X0 = 50, PLOT_X1 = 390, PLOT_Y0 = 10, PLOT_Y1 = 210;
            const plotW = PLOT_X1 - PLOT_X0, plotH = PLOT_Y1 - PLOT_Y0;
            const toY = v => PLOT_Y1 - ((v - Y_MIN) / (Y_MAX - Y_MIN)) * plotH;
            const toX = i => PLOT_X0 + (i / Math.max(hrData.length - 1, 1)) * plotW;
            const makePts = field => hrData.map((s, i) => s[field] != null ? { x: toX(i), y: toY(s[field]) } : null).filter(Boolean);
            const restPts = makePts('rest_heart_rate'), effortPts = makePts('effort_heart_rate');
            const labelStep = hrData.length <= 7 ? 1 : hrData.length <= 15 ? 2 : hrData.length <= 30 ? 3 : 7;
            return (
              <svg width="100%" viewBox="0 0 420 240" style={CHART_SVG_STYLE}>
                {[0, 40, 80, 120, 160].map(v => (
                  <g key={v}>
                    <text x="40" y={toY(v) + 4} fontSize="11" fill={UI.textAxis} textAnchor="end">{v}</text>
                    <line x1={PLOT_X0} y1={toY(v)} x2={PLOT_X1} y2={toY(v)} stroke="var(--color-grid)" strokeWidth="1" />
                  </g>
                ))}
                {effortPts.length >= 2 && <path d={smoothPath(effortPts)} fill="none" stroke="var(--sport)" strokeWidth="2.5" strokeLinecap="round" />}
                {restPts.length >= 2 && <path d={smoothPath(restPts)} fill="none" stroke="var(--sport-repos)" strokeWidth="2.5" strokeLinecap="round" />}
                {hrData.map((s, i) => {
                  const phaseColor = phases[String(s.date)] ? (PHASE_COLORS_MAP[phases[String(s.date)]] || UI.surfaceEmpty) : UI.surfaceEmpty;
                  return (
                    <g key={i}>
                      {i % labelStep === 0 && <text x={toX(i)} y="230" fontSize="10" fill={UI.textAxis} textAnchor="middle">{['D','L','M','M','J','V','S'][new Date(s.date).getDay()]}</text>}
                      <circle cx={toX(i)} cy="238" r="3" fill={phaseColor} />
                    </g>
                  );
                })}
              </svg>
            );
          }
          if (historyChart === 'rpe') {
            const data = historyRpeData;
            if (!data.some(e => e.rpe != null)) return <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '2rem 0' }}>Aucune donnée RPE</p>;
            const byDate = Object.fromEntries(data.map(s => [String(s.date), s]));
            const bars = [];
            for (let di = historyRange - 1; di >= 0; di--) {
              const d = new Date(); d.setDate(d.getDate() - di);
              const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
              const entry = byDate[dateStr];
              bars.push({ date: dateStr, dayLabel: DAY_LABELS[(d.getDay()+6)%7], rpe: entry?.rpe ?? null, isRest: !entry || !!entry?.is_rest_day });
            }
            const n = bars.length;
            const labelStep = n <= 7 ? 1 : n <= 15 ? 2 : n <= 30 ? 3 : 7;
            const X0 = 20, X1 = 390, Y_TOP = 10, Y_BOT = 160, plotW = X1 - X0, plotH = Y_BOT - Y_TOP;
            const toX = i => X0 + (i / Math.max(n-1,1)) * plotW;
            const toY = rpe => Y_BOT - (rpe / 10) * plotH;
            const BAR_W = Math.max(2, Math.min(14, Math.floor(plotW / Math.max(n,1)) - 1));
            return (
              <svg width="100%" viewBox="0 0 410 190" style={CHART_SVG_STYLE}>
                {[10,5,0].map(v => (
                  <g key={v}>
                    <text x={X0-4} y={toY(v)+4} fontSize="10" fill={UI.textAxis} textAnchor="end">{v}</text>
                    <line x1={X0} y1={toY(v)} x2={X1} y2={toY(v)} stroke="var(--color-grid)" strokeWidth="1" />
                  </g>
                ))}
                {bars.map((entry, i) => {
                  const barColor = entry.rpe != null ? (entry.rpe <= 5 ? 'var(--sport-rpe-facile)' : 'var(--sport-primary)') : 'transparent';
                  const phaseColor = phases[entry.date] ? (PHASE_COLORS_MAP[phases[entry.date]] || UI.surfaceEmpty) : UI.surfaceEmpty;
                  const barH = entry.rpe != null ? (entry.rpe / 10) * plotH : 0;
                  return (
                    <g key={i}>
                      {entry.rpe != null && <rect x={toX(i)-BAR_W/2} y={toY(entry.rpe)} width={BAR_W} height={barH} rx="2" fill={barColor} />}
                      {i % labelStep === 0 && <text x={toX(i)} y="175" fontSize="9" fill={UI.textAxis} textAnchor="middle">{entry.dayLabel}</text>}
                      <circle cx={toX(i)} cy="185" r="3" fill={phaseColor} />
                    </g>
                  );
                })}
              </svg>
            );
          }
          if (historyChart === 'breath') {
            const data = historyBreathData;
            const BREATH_LEVELS = { 'Pas essoufflée': 1, 'Modérée': 2, 'Très essoufflée': 3 };
            const breathDays = data.map(s => ({
              dayLabel: DAY_LABELS[(new Date(s.date).getDay()+6)%7],
              level: s.breathlessness ? (BREATH_LEVELS[s.breathlessness] ?? 0) : 0,
              date: String(s.date),
            }));
            if (breathDays.length === 0) return <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '2rem 0' }}>Aucune donnée</p>;
            const n = breathDays.length;
            const labelStep = n <= 7 ? 1 : n <= 15 ? 2 : n <= 30 ? 3 : 7;
            const PLOT_X0 = 48, PLOT_X1 = 390, PLOT_Y0 = 32, PLOT_Y1 = 132;
            const toX = i => PLOT_X0 + (i / Math.max(n-1,1)) * (PLOT_X1 - PLOT_X0);
            const toY = level => PLOT_Y1 - ((level+1)/4) * (PLOT_Y1 - PLOT_Y0);
            const pts = breathDays.map((d, i) => ({ x: toX(i), y: toY(d.level) }));
            const linePath = monotonePath(pts);
            const areaPath = linePath + ` L ${pts[n-1].x},${PLOT_Y1} L ${pts[0].x},${PLOT_Y1} Z`;
            const Y_LABELS = { 0: 'Repos', 1: 'Faible', 2: 'Modéré', 3: 'Intense' };
            return (
              <svg width="100%" viewBox="0 0 420 182" style={CHART_SVG_STYLE}>
                <defs>
                  <linearGradient id="breathGradH" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={SPORT.soufflement.intense} stopOpacity="0.5" />
                    <stop offset="66%"  stopColor={SPORT.soufflement.faible} stopOpacity="0.3" />
                    <stop offset="100%" stopColor={SPORT.soufflement.repos} stopOpacity="0.2" />
                  </linearGradient>
                </defs>
                {[0,1,2,3].map(level => (
                  <g key={level}>
                    <text x={PLOT_X0-4} y={toY(level)+4} fontSize="8" fill={UI.textAxis} textAnchor="end">{Y_LABELS[level]}</text>
                    <line x1={PLOT_X0} y1={toY(level)} x2={PLOT_X1} y2={toY(level)} stroke="var(--color-grid)" strokeWidth="1" />
                  </g>
                ))}
                <path d={areaPath} fill="url(#breathGradH)" />
                <path d={linePath} fill="none" stroke="var(--sport-primary)" strokeWidth="2" strokeLinecap="round" />
                {breathDays.map((d, i) => {
                  const phaseColor = phases[d.date] ? (PHASE_COLORS_MAP[phases[d.date]] || UI.surfaceEmpty) : UI.surfaceEmpty;
                  return (
                    <g key={i}>
                      {i % labelStep === 0 && <text x={toX(i)} y="170" fontSize="9" fill={UI.textAxis} textAnchor="middle">{d.dayLabel}</text>}
                      <circle cx={toX(i)} cy="179" r="3" fill={phaseColor} />
                    </g>
                  );
                })}
              </svg>
            );
          }
          return null;
        };
        return (
          <div className="sport-page__history-overlay" onClick={() => setHistoryChart(null)}>
            <div className="sport-page__history-modal" onClick={e => e.stopPropagation()}>
              <div className="sport-page__history-header">
                <h3 className="sport-page__history-title">{TITLES[historyChart]}</h3>
                <button className="sport-page__history-close" onClick={() => setHistoryChart(null)} aria-label="Fermer">✕</button>
              </div>
              <div className="sport-page__history-periods">
                {[90, 180, 365].map(r => (
                  <button
                    key={r}
                    className={`sport-page__history-period${historyRange === r ? ' sport-page__history-period--active' : ''}`}
                    onClick={() => setHistoryRange(r)}
                  >
                    {r === 365 ? '1 an' : `${r}j`}
                  </button>
                ))}
              </div>
              {renderHistoryContent()}
              {historyChart === 'hr' && (
                <div className="sport-page__legend" style={{ justifyContent: 'center', marginTop: 8 }}>
                  <div className="sport-page__legend-item">
                    <span style={{ display: 'inline-block', width: 20, height: 3, borderRadius: 2, background: 'var(--sport-repos)' }} />
                    <span>FC Repos</span>
                  </div>
                  <div className="sport-page__legend-item">
                    <span style={{ display: 'inline-block', width: 20, height: 3, borderRadius: 2, background: 'var(--sport)' }} />
                    <span>FC Effort</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Sport;
